/*!
 * © 2024. The EGT Universe. All Rights Reserved.
 * License: Requires ACTIVE AppArtisan Subscription.
 * For more information visit: https://egtuniverse.com/legal
 *
 * Created for Universe App Tools.
 *
 * Created by Justin K Kazmierczak.
 */
var _ = require("../../../uam/module.js")(module.exports, {
  namespace: "ua.dashboard.list",
  title: "Dashboard List View",
  description: "A reusable dashboard list view shell with search, filters, view toggle, results area, and pagination. Populated client-side via DashboardAPI.",
  styling: {
    styles: [
      {
        selector: ".ua-dashboard-list-shell",
        styles: [
          "border-radius: 1rem;",
          "padding: 1rem;",
          "background: rgba(15, 23, 42, .68);",
          "backdrop-filter: blur(1.125rem);",
          "-webkit-backdrop-filter: blur(1.125rem);",
          "border: .0625rem solid rgba(255, 255, 255, .12);",
          "box-shadow: 0 .75rem 2rem rgba(0, 0, 0, .18);",
          "color: var(--bs-light, #f8f9fa);"
        ]
      },
      {
        selector: ".ua-dashboard-list-toolbar",
        styles: [
          "display: flex;",
          "align-items: center;",
          "gap: .5rem;",
          "flex-wrap: wrap;",
          "margin-bottom: .75rem;"
        ]
      },
      {
        selector: ".ua-dashboard-list-toolbar .ua-dashboard-search",
        styles: [
          "flex: 1 1 14rem;",
          "min-width: 10rem;"
        ]
      },
      {
        selector: ".ua-dashboard-toolbar-spacer",
        styles: [
          "flex: 1 1 auto;"
        ]
      },
      {
        selector: ".ua-dashboard-active-filters",
        styles: [
          "display: flex;",
          "align-items: center;",
          "gap: .5rem;",
          "flex-wrap: wrap;",
          "margin-bottom: 1rem;",
          "background: rgba(255, 255, 255, .08);",
          "border: .0625rem solid rgba(255, 255, 255, .08);",
          "border-radius: .85rem;"
        ]
      },
      {
        selector: ".ua-dashboard-active-filters.d-none",
        styles: [
          "display: none !important;"
        ]
      },
      {
        selector: ".ua-dashboard-quick-toggles",
        styles: [
          "display: inline-flex;",
          "align-items: center;",
          "gap: .35rem;",
          "flex-wrap: wrap;"
        ]
      },
      {
        selector: ".ua-dashboard-active-filter-chips",
        styles: [
          "display: inline-flex;",
          "align-items: center;",
          "gap: .35rem;",
          "flex-wrap: wrap;"
        ]
      },
      {
        selector: ".ua-dashboard-list-results",
        styles: [
          "min-height: 4rem;",
          "padding: .25rem 0;"
        ]
      },
      {
        selector: ".ua-dashboard-list-skeleton",
        styles: [
          "display: flex;",
          "flex-direction: column;",
          "gap: .5rem;"
        ]
      },
      {
        selector: ".ua-dashboard-skeleton-row",
        styles: [
          "height: 2.5rem;",
          "border-radius: .5rem;",
          "background: linear-gradient(90deg, var(--bs-secondary-bg) 25%, var(--bs-tertiary-bg) 50%, var(--bs-secondary-bg) 75%);",
          "background-size: 200% 100%;",
          "animation: ua-skeleton-shimmer 1.4s infinite;"
        ]
      },
      {
        selector: "@keyframes ua-skeleton-shimmer",
        styles: [
          "0% { background-position: 200% 0; }",
          "100% { background-position: -200% 0; }"
        ]
      },
      {
        selector: ".ua-dashboard-list-footer",
        styles: [
          "display: flex;",
          "align-items: center;",
          "justify-content: space-between;",
          "flex-wrap: wrap;",
          "gap: .5rem;",
          "margin-top: 1rem;",
          "padding-top: .75rem;",
          "padding-bottom: .25rem",
          "border-top: .0625rem solid rgba(255, 255, 255, .08);"
        ]
      },
      {
        selector: ".ua-dashboard-list-pagination",
        styles: [
          "display: flex;",
          "align-items: center;",
          "justify-content: flex-end;",
          "gap: .5rem;",
          "margin-left: auto;"
        ]
      },
      {
        device: "phone",
        selector: ".ua-dashboard-list-pagination",
        styles: [
          "justify-content: flex-start;",
          "margin-left: 0;",
          "width: 100%;"
        ]
      },
      {
        selector: ".ua-dashboard-list-toolbar .ua-dashboard-filter-chip",
        styles: [
          "display: inline-flex;",
          "align-items: center;",
          "gap: .35rem;"
        ]
      },
      {
        selector: ".ua-dashboard-item-icon.ua-dashboard-item-thumb",
        styles: [
          "width: 3rem;",
          "height: 3rem;",
          "min-width: 3rem;",
          "border-radius: .45rem;",
          "overflow: hidden;",
          "background: rgba(0,0,0,.06);"
        ]
      },
      {
        selector: ".ua-dashboard-item-icon.ua-dashboard-item-thumb img",
        styles: [
          "width: 100%;",
          "height: 100%;",
          "object-fit: cover;",
          "transition: object-fit 120ms ease-in-out;"
        ]
      },
      {
        selector: ".ua-dashboard-item-icon.ua-dashboard-item-thumb:hover img",
        styles: [
          "object-fit: contain;",
          "background: var(--bs-body-bg);"
        ]
      }
    ]
  },
  fields: {
    "page-namespace": {
      title: "Page Namespace",
      type: "string",
      description: "The dashboard page namespace used to build API calls and navigation links.",
      required: true
    },
    "api-url": {
      title: "API URL",
      type: "string",
      description: "Full API base URL for this page (e.g. /api/my/app.site-content). Overrides the default /api/<namespace> construction. Set from context.page.routes.api in content functions."
    },
    "page-url": {
      title: "Page URL",
      type: "string",
      description: "Base page URL for building edit/detail links (e.g. /dashboard/my/app-site-content). Overrides the default /dashboard/<slug> construction. Set from context.page.routes.page in content functions."
    },
    "searchable": {
      title: "Searchable",
      type: "boolean",
      description: "Show a search input in the toolbar.",
      default: true
    },
    "view-mode": {
      title: "View Mode",
      type: "string",
      description: "Display mode: list (line items) or card (gallery/preview grid).",
      list: ["list", "card"],
      default: "list"
    },
    "view-options": {
      title: "View Options",
      type: "array",
      description: "View toggle options with rows: { name, title, icon }."
    },
    "empty-message": {
      title: "Empty Message",
      type: "string",
      description: "Message shown when no results are found.",
      default: "No items found."
    },
    "empty-description": {
      title: "Empty Description",
      type: "string",
      description: "Optional supporting copy shown below the empty message. Use for orientation or next-step guidance."
    },
    "filters": {
      title: "Filters",
      type: "array",
      description: "Optional filter definitions. Rows support { name, label, description, options:[{value,label,icon}], icon, buttonLabel, preview, multi, iconOnly, quickToggle }."
    },
    "page-size": {
      title: "Page Size",
      type: "noattr.number",
      description: "Number of items per page.",
      default: 25
    }
  }
});

var uai = require("../interface/interface.js");

function normalizeIconClass(icon) {
  var text = (icon || "").toString().trim();
  if (!text) return "";
  if (text.indexOf("bi ") === 0) return text;
  if (text.indexOf("bi-") === 0) return "bi " + text;
  return text;
}

function normalizeViewOptions(raw) {
  var source = raw instanceof Array ? raw : [];
  var list = [];

  for (var i = 0; i < source.length; i++) {
    var row = source[i] || {};
    var name = (row.name || row.id || row.value || "").toString().trim().toLowerCase();
    if (!name) continue;
    list.push({
      name: name,
      title: (row.title || row.label || name).toString(),
      icon: normalizeIconClass(row.icon || "")
    });
  }

  if (list.length === 0) {
    return [
      { name: "list", title: "List View", icon: "bi bi-list-task" },
      { name: "card", title: "Card View", icon: "bi bi-grid-3x3-gap" }
    ];
  }

  return list;
}

/**
 * Render the dashboard list view shell.
 * The results area is populated client-side via DashboardAPI and pageinit handlers.
 */
exports.render = function(options) {
  var pageNamespace = (options["page-namespace"] || "").toString().trim();
  var apiUrl        = (options["api-url"]  || "").toString().trim();
  var pageUrl       = (options["page-url"] || "").toString().trim();
  var searchable    = options.searchable !== false;
  var viewMode      = (options["view-mode"] || "list").toString().toLowerCase();
  var emptyMessage  = (options["empty-message"] || "No items found.").toString();
  var emptyDescription = (options["empty-description"] || "").toString();
  var filters       = options.filters instanceof Array ? options.filters : [];
  var pageSize      = typeof options["page-size"] === "number" ? options["page-size"] : 25;
  var viewOptions   = normalizeViewOptions(options["view-options"] || []);

  var viewMatch = viewOptions.some(function(row) { return row.name === viewMode; });
  if (!viewMatch) {
    viewMode = (viewOptions[0] || {}).name || "list";
  }

  var uid = uai.generateRandomID(8);
  var resultsId = "ua-dash-results-" + uid;
  var paginationId = "ua-dash-pagination-" + uid;
  var countId = "ua-dash-count-" + uid;

  var controls = [];

  controls.push({
    n: "ua.button.toggle",
    name: "dashboard-view-mode",
    title: "Change view",
    mode: "icon",
    style: "btn btn-outline-secondary btn-sm",
    options: viewOptions,
    value: viewMode,
    "data-dashboard-view-toggle": "true",
    "aria-label": "Change view"
  });

  var skeletonRows = [];
  for (var s = 0; s < 4; s++) {
    skeletonRows.push({ n: "div", c: "ua-dashboard-skeleton-row" });
  }

  return {
    n: "div",
    c: "ua-dashboard-list-shell",
    "data-ua-dashboard-list": "true",
    "data-dashboard-page-namespace": pageNamespace,
    "data-dashboard-api-url":  apiUrl  || undefined,
    "data-dashboard-page-url": pageUrl || undefined,
    "data-dashboard-view-mode": viewMode,
    "data-dashboard-view-options": JSON.stringify(viewOptions),
    "data-dashboard-page-size": pageSize.toString(),
    "data-dashboard-empty-message": emptyMessage,
    "data-dashboard-empty-description": emptyDescription || undefined,
    i: [
      {
        n: "ua.filter",
        name: "dashboardListToolbar" + uid,
        searchable: searchable,
        "search-placeholder": "Search...",
        filters: filters,
        controls: controls,
        "show-active-filters": true
      },
      {
        n: "div",
        id: resultsId,
        c: "ua-dashboard-list-results",
        "data-dashboard-results": "true",
        i: {
          n: "div",
          c: "ua-dashboard-list-skeleton",
          i: skeletonRows
        }
      },
      {
        n: "div",
        c: "ua-dashboard-list-footer",
        i: [
          {
            n: "div",
            id: countId,
            c: "text-muted small",
            "data-dashboard-count": "true",
            i: ""
          },
          {
            n: "div",
            id: paginationId,
            c: "ua-dashboard-list-pagination",
            "data-dashboard-pagination": "true",
            i: ""
          }
        ]
      }
    ]
  };
};
